import { ScanLine, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAuthState } from "@/app/context";
import { QrScanner } from "@/features/pickup/QrScanner";
import { useScanInput } from "@/features/sale/hooks/useScanInput";

/**
 * 返品 — find the sale (design/05 §3.6): scan the receipt's QR (QR reader or the camera), or type
 * the receipt number / the P- order number, or scan one of the cards (cert / label).
 */
export function OrderLookup({
  onCode,
  busy,
  error,
}: {
  onCode: (code: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  const unlocked = useAuthState().kind === "unlocked";
  const scan = useScanInput({ onScan: onCode, onSearch: () => undefined, enabled: unlocked });
  return (
    <div className="grid h-full min-h-0 grid-cols-[minmax(0,1fr)_24rem] gap-6 p-6">
      <section aria-labelledby="return-find" className="flex flex-col gap-4">
        <h1 id="return-find" className="text-2xl font-semibold">
          {t("returns.find_title")}
        </h1>
        <p className="text-lg text-muted-foreground">{t("returns.find_help")}</p>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const value = scan.value.trim();
            if (value) {
              scan.setValue("");
              onCode(value);
            }
          }}
        >
          <label className="flex min-h-pos-touch flex-1 items-center gap-2 rounded-md border px-3 focus-within:ring-2 focus-within:ring-ring">
            <ScanLine aria-hidden className="size-6 shrink-0" />
            <span className="sr-only">{t("returns.find_label")}</span>
            <input
              {...scan.inputProps}
              autoFocus
              autoComplete="off"
              enterKeyHint="search"
              placeholder={t("returns.find_placeholder")}
              className="min-w-0 flex-1 bg-transparent text-xl outline-none"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground disabled:opacity-50"
          >
            <Search aria-hidden />
            {t("returns.find_submit")}
          </button>
        </form>
        {busy ? <p aria-live="polite">{t("app.loading")}</p> : null}
        {error ? (
          <p
            role="alert"
            className="rounded-md border-2 border-status-problem p-3 text-lg text-pos-problem"
          >
            {error}
          </p>
        ) : null}
      </section>
      <aside>
        <QrScanner onCode={onCode} />
      </aside>
    </div>
  );
}
